import type { SharePointAttachmentRef } from '@/integration/types.js';
import type { SharePointItem } from '@/lib/api/sharepoint.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

/**
 * Turn a file in the SharePoint tree into the descriptor a message references it by. Nothing is copied
 * and nothing is uploaded: the keys are an ADDRESS, and the agent reads the file through SharePoint when
 * the turn runs. That is also why the reference is not pinned to a version — `name` is the only snapshot,
 * and it exists so the chip stays readable after someone renames the file.
 *
 * The address names the SharePoint it belongs to, because a workspace reaches more than one and Graph ids
 * repeat between tenants. `connectionId` is written only when there IS one: a missing `connection_id`
 * means the workspace's shared connection, which is what keeps every message already sent readable, and
 * an empty string would be a connection id that matches nothing.
 *
 * `web_url` is a convenience for a reader, so anything that is not a plain http(s) URL is left out here
 * rather than sent for the server to refuse. The server validates it again — it does not trust this — but
 * a link the client itself would not render has no business travelling.
 */
export function sharePointAttachmentRef({
  connectionId,
  siteId,
  driveId,
  item,
}: {
  /** The SharePoint the row was found in, or `null` for the one the service resolves. */
  connectionId: string | null;
  siteId: string;
  driveId: string;
  item: SharePointItem;
}): SharePointAttachmentRef {
  const webUrl = safeHttpUrl(item.webUrl);
  return {
    ...(connectionId === null || connectionId === ''
      ? {}
      : { connection_id: connectionId }),
    site_id: siteId,
    drive_id: driveId,
    item_id: item.itemId,
    name: item.name,
    ...(webUrl === '' ? {} : { web_url: webUrl }),
  };
}

/**
 * What makes two SharePoint references the same file — the TRIPLE, never the item id alone and never the
 * pair either. A Graph `item_id` is unique only inside its own library, and a `drive_id` only inside its
 * own tenant, so the same pair in two SharePoints addresses two unrelated documents: on the pair alone two
 * picks would collapse into one, and one chip would stand for two files.
 *
 * A reference naming no connection keys on the empty string, a value no connection id can take, so the
 * shared connection is a lane of its own rather than a wildcard matching every named one.
 *
 * The separator is a NUL because no Graph id contains one, so no two different triples can flatten to the
 * same string.
 */
export function sharePointRefKey(
  ref: Pick<SharePointAttachmentRef, 'connection_id' | 'drive_id' | 'item_id'>,
): string {
  return `${ref.connection_id ?? ''}\u0000${ref.drive_id}\u0000${ref.item_id}`;
}
